import { Stack } from 'src/design/atoms/Stack';
import { Select } from 'src/design/atoms/Select';
import moment from 'moment';
import React from 'react';
import { formatDate } from 'src/utils';
import { getCurrentWorkWeek, getWorkWeekRange } from 'src/utils/workWeek';
import { IDateOption } from 'src/routes/Jobs/Timesheets/types';

/**
 * Generates week options for a specific year using canonical Sat→Fri work-week helpers.
 */
export const generateWeeksForYear = (year: number): IDateOption[] => {
  const weeks: IDateOption[] = [];
  const now = moment();

  // Use mid-year to determine how many ISO weeks the year contains (safe upper bound).
  const midYear = moment([year, 6, 1]);
  const weeksInYear = midYear.weeksInYear();

  for (let weekNum = 1; weekNum <= weeksInYear; weekNum++) {
    const { start, end } = getWorkWeekRange(weekNum, year);
    const weekStart = moment(start).hour(12);
    const weekEnd = moment(end).hour(12);
    const isCurrentWeek = now.isBetween(weekStart, weekEnd, 'day', '[]');

    weeks.push({
      id: `${year}-${weekNum}`,
      startDate: weekStart.toDate(),
      endDate: weekEnd.toDate(),
      prefix: isCurrentWeek ? 'Current Week - ' : undefined,
      label: `${formatDate(weekStart.toDate(), 'ddd (M/D/YY)')} - ${formatDate(
        weekEnd.toDate(),
        'ddd (M/D/YY)',
      )}`,
    });
  }

  return weeks;
};

/**
 * Generates year options (current year and previous years)
 */
const generateYearOptions = (yearsBack = 3): string[] => {
  const currentYear = moment().year();
  const years: string[] = [];

  for (let i = 0; i <= yearsBack; i++) {
    years.push(`${currentYear - i}`);
  }

  return years;
};

interface IAuditWeekSelectorProps {
  value: IDateOption | null;
  onChange: (value: IDateOption | null) => void;
  label?: string;
  placeholder?: string;
  yearsBack?: number;
}

export const AuditWeekSelector: React.FC<IAuditWeekSelectorProps> = ({
  onChange,
  value,
  label,
  placeholder = 'Select week...',
  yearsBack = 3,
}) => {
  const yearOptions = React.useMemo(
    () => generateYearOptions(yearsBack),
    [yearsBack],
  );

  // Extract year from current value or default to current year
  const [selectedYear, setSelectedYear] = React.useState<string>(() => {
    if (value?.id && value.id.includes('-')) {
      return value.id.split('-')[0];
    }
    return `${moment().year()}`;
  });

  // Generate weeks for the selected year
  const weekOptions = React.useMemo(
    () => generateWeeksForYear(Number(selectedYear)),
    [selectedYear],
  );

  // Format weeks for the dropdown
  const weekData = React.useMemo(() => {
    return weekOptions.map((week) => {
      const weekNum = week.id.split('-')[1];
      return {
        value: week.id,
        label: `${week.prefix || ''}Week ${weekNum}: ${week.label}`,
      };
    });
  }, [weekOptions]);

  const handleYearChange = (year: string | null) => {
    if (year) {
      setSelectedYear(year);
      // Clear week selection when year changes
      onChange(null);
    }
  };

  const handleWeekChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedWeek =
        weekOptions.find((w) => w.id === selectedValue) || null;
      onChange(selectedWeek);
    } else {
      onChange(null);
    }
  };

  return (
    <Stack gap="xs">
      <Select
        label={label ? 'Year' : undefined}
        placeholder="Year"
        data={yearOptions}
        value={selectedYear}
        onChange={handleYearChange}
        searchable
      />
      <Select
        label={label ? label : undefined}
        placeholder={placeholder}
        data={weekData}
        value={value?.id || null}
        onChange={handleWeekChange}
        searchable
        clearable
      />
    </Stack>
  );
};

/**
 * Gets the current week as an IDateOption with year-aware ID
 */
export const getCurrentWeekOption = (): IDateOption => {
  const { year } = getCurrentWorkWeek();
  const weeks = generateWeeksForYear(year);
  const current = weeks.find((w) => w.prefix === 'Current Week - ');
  if (current) return current;

  // Fallback: check adjacent year (near year boundaries)
  const now = moment();
  const altYear = now.month() === 0 ? year - 1 : year + 1;
  const altWeeks = generateWeeksForYear(altYear);
  return altWeeks.find((w) => w.prefix === 'Current Week - ') ?? weeks[0];
};
